import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import { Modal, Inp } from "./Enquiries";
import PatientAttachments from "../../components/PatientAttachments";
import ToothChart from "../../components/ToothChart";
import TreatmentPlansPanel from "../../components/TreatmentPlansPanel";
import { ArrowLeft, Plus, Tooth, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PatientDetail() {
  const { id } = useParams();
  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showT, setShowT] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const load = async () => {
    const [p, a, t, i, d] = await Promise.all([
      api.get(`/patients/${id}`),
      api.get(`/appointments?patient_id=${id}`),
      api.get(`/treatments?patient_id=${id}`),
      api.get(`/invoices?patient_id=${id}`),
      api.get(`/doctors`),
    ]);
    setPatient(p.data);
    setAppointments(a.data);
    setTreatments(t.data);
    setInvoices(i.data);
    setDoctors(d.data);
  };

  useEffect(() => {
    load();
  }, [id]);

  const completedTreatments = treatments.filter((item) => item.performed_at).length;
  const latestTreatment = treatments[0] || null;

  if (!patient) return <div className="p-12 text-sm text-zinc-500">Loading...</div>;

  return (
    <div data-testid="patient-detail">
      <PageHeader
        title={patient.name}
        subtitle={`${patient.phone || ""}${patient.email ? " - " + patient.email : ""}`}
        actions={
          <Link to="/staff/patients" className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-700 hover:text-[#E60000]">
            <ArrowLeft size={16} weight="bold" /> Back
          </Link>
        }
      />
      <div className="px-6 sm:px-10 py-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Profile</h3>
            <button onClick={() => setShowEdit(true)} className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000] inline-flex items-center gap-1" data-testid="edit-patient-btn">
              <Pencil size={12} weight="bold" /> Edit
            </button>
          </div>
          {(() => {
            const dr = doctors.find((d) => d.id === patient.primary_doctor_id);
            return <Field k="Primary Doctor" v={dr?.name} accent={!!dr} />;
          })()}
          <Field k="Date of birth" v={patient.dob} />
          <Field k="Gender" v={patient.gender} />
          <Field k="Blood group" v={patient.blood_group} />
          <Field k="Address" v={patient.address} />
          <Field k="Allergies" v={patient.allergies} accent />
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1">Medical history</div>
            <div className="text-sm text-zinc-900 whitespace-pre-wrap">{patient.medical_history || "-"}</div>
          </div>

          <div className="mt-6 border-t border-zinc-200 pt-4">
            <PatientAttachments patientId={id} />
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-zinc-200 rounded-sm p-4">
            <ToothChart patientId={id} />
          </div>

          <div className="bg-white border border-zinc-200 rounded-sm p-4">
            <TreatmentPlansPanel patientId={id} />
          </div>

          <div className="bg-white border border-zinc-200 rounded-sm p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">
                  Revisit Snapshot
                </div>
                <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Treatment history
                </h3>
                <p className="text-sm text-zinc-500 mt-1">
                  Keep previous procedures easy to review before the next visit.
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black tracking-tight text-zinc-900">{completedTreatments}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Recorded treatments</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="border border-zinc-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1">Latest treatment</div>
                <div className="text-sm font-semibold text-zinc-900">{latestTreatment?.title || "No treatment recorded yet"}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  {latestTreatment?.performed_at?.slice(0, 10) || "Add the first treatment record to start the patient history."}
                </div>
              </div>
              <div className="border border-zinc-200 rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1">Medical history</div>
                <div className="text-sm text-zinc-900 whitespace-pre-wrap">
                  {patient.medical_history || "No medical history added yet."}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-sm">
            <Header title="Appointments" />
            <List items={appointments.map((a) => ({
              key: a.id,
              title: a.treatment || "Consultation",
              sub: `${new Date(a.starts_at).toLocaleString()} - ${a.status}`,
            }))} empty="No appointments yet" />
          </div>

          <div className="bg-white border border-zinc-200 rounded-sm">
            <Header title="Treatment timeline" action={
              <button onClick={() => setShowT(true)} className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000] inline-flex items-center gap-1" data-testid="add-treatment-btn">
                <Plus size={12} weight="bold" /> Add treatment
              </button>
            } />
            <List items={treatments.map((t) => ({
              key: t.id,
              title: t.title,
              sub: `${t.performed_at?.slice(0, 10) || "-"}${t.doctor ? " - " + t.doctor : ""}${t.cost ? " - Rs " + t.cost : ""}`,
              icon: Tooth,
              desc: t.description,
            }))} empty="No treatments recorded" />
          </div>

          <div className="bg-white border border-zinc-200 rounded-sm">
            <Header title="Invoices" />
            <List items={invoices.map((i) => ({
              key: i.id,
              title: `INV-${i.id.slice(0, 6).toUpperCase()} - Rs ${i.total.toFixed(0)}`,
              sub: `${i.created_at?.slice(0, 10)} - ${i.status} - Paid Rs ${i.paid.toFixed(0)} - Balance Rs ${i.balance.toFixed(0)}`,
            }))} empty="No invoices yet" />
          </div>
        </div>
      </div>

      {showT && <AddTreatment patientId={id} doctors={doctors} onClose={() => setShowT(false)} onSaved={() => { setShowT(false); load(); }} />}
      {showEdit && <EditPatient patient={patient} doctors={doctors} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
    </div>
  );
}

function EditPatient({ patient, doctors, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: patient.name || "",
    phone: patient.phone || "",
    email: patient.email || "",
    dob: patient.dob || "",
    gender: patient.gender || "male",
    address: patient.address || "",
    blood_group: patient.blood_group || "",
    allergies: patient.allergies || "",
    medical_history: patient.medical_history || "",
    primary_doctor_id: patient.primary_doctor_id || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/patients/${patient.id}`, { ...form, email: form.email || null, primary_doctor_id: form.primary_doctor_id || null });
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Edit patient">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Inp label="Name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="ep-name" />
        <Inp label="Phone" v={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="ep-phone" />
        <Inp label="Email" v={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="ep-email" />
        <Inp label="DOB" type="date" v={form.dob} onChange={(v) => setForm({ ...form, dob: v })} testid="ep-dob" />
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Primary doctor</label>
          <select value={form.primary_doctor_id} onChange={(e) => setForm({ ...form, primary_doctor_id: e.target.value })}
            className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white focus:outline-none focus:border-zinc-900" data-testid="ep-doctor">
            <option value="">- none -</option>
            {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <Inp label="Blood group" v={form.blood_group} onChange={(v) => setForm({ ...form, blood_group: v })} testid="ep-blood" />
        <Inp label="Allergies" v={form.allergies} onChange={(v) => setForm({ ...form, allergies: v })} testid="ep-allergies" />
        <div className="sm:col-span-2"><Inp label="Address" v={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="ep-address" /></div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Medical history</label>
          <textarea rows={3} value={form.medical_history} onChange={(e) => setForm({ ...form, medical_history: e.target.value })}
            className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-zinc-900" data-testid="ep-history" />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm">Cancel</button>
        <button onClick={save} disabled={busy} className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60" data-testid="ep-save">
          {busy ? "Saving..." : "Save"}
        </button>
      </div>
    </Modal>
  );
}

function Field({ k, v, accent }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-0.5">{k}</div>
      <div className={`text-sm ${accent ? "text-[#E60000] font-semibold" : "text-zinc-900"}`}>{v || "-"}</div>
    </div>
  );
}

function Header({ title, action }) {
  return (
    <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
      <h3 className="font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h3>
      {action}
    </div>
  );
}

function List({ items, empty }) {
  if (!items.length) return <div className="p-6 text-sm text-zinc-500">{empty}</div>;
  return (
    <div className="divide-y divide-zinc-100">
      {items.map((it) => (
        <div key={it.key} className="px-5 py-3 flex items-start gap-3">
          {it.icon ? <it.icon size={18} weight="duotone" color="#E60000" className="mt-0.5" /> : <span className="w-1 h-1 rounded-full bg-zinc-400 mt-3" />}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-zinc-900">{it.title}</div>
            <div className="text-xs text-zinc-500">{it.sub}</div>
            {it.desc && <div className="text-sm text-zinc-700 mt-1">{it.desc}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function AddTreatment({ patientId, doctors = [], onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", cost: 0, doctor_id: "", performed_at: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/treatments", { patient_id: patientId, ...form, cost: Number(form.cost) || 0 });
      toast.success("Treatment added");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="New treatment record">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><Inp label="Title" v={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="t-title" /></div>
        <Inp label="Cost (Rs)" type="number" v={form.cost} onChange={(v) => setForm({ ...form, cost: v })} testid="t-cost" />
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Doctor</label>
          <select value={form.doctor_id} onChange={(e) => setForm({ ...form, doctor_id: e.target.value })}
            className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white focus:outline-none focus:border-zinc-900" data-testid="t-doctor">
            <option value="">- pick doctor -</option>
            {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <Inp label="Date" type="date" v={form.performed_at} onChange={(v) => setForm({ ...form, performed_at: v })} testid="t-date" />
      </div>
      <div className="mt-3">
        <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Notes</label>
        <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-zinc-900" data-testid="t-desc" />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm">Cancel</button>
        <button onClick={save} disabled={busy} className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60" data-testid="t-save">
          {busy ? "Saving..." : "Save"}
        </button>
      </div>
    </Modal>
  );
}
